import { useRef, useState } from 'react';
import { Workbook } from '@fortune-sheet/react';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { useSheetEditorDrawerLogic } from './useSheetEditorDrawerLogic';
import { FortuneSheet } from './types';
import { MIN_ROW_HEIGHT } from './fileTypes';
import { useColumnAutofit } from './useColumnAutofit';
import { useOutsideClick } from '../../utils/constants/hooks';
import { editIconPurple } from '../../assets/hostedassets';
import RenameReportDialog from '../../modules/Reports/components/RenameReportDialog/RenameReportDialog';
import WhiteCheckIcon from '../Reports/WhiteCheckIcon';
import styles from './SheetEditorDrawer.module.scss';
import '@fortune-sheet/react/dist/index.css';

export interface SheetEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Original file name; used as the drawer title and download base name. */
    fileName: string;
    /** In-memory sheets (import / composed). When provided, renders directly — skips URL fetch. */
    sheets?: FortuneSheet[];
    /** Presigned URL of the xlsx/csv file to edit (legacy fetch path). */
    fileUrl?: string;
    /** Resolved MIME type — drives xlsx vs csv parsing on the fetch path. */
    mimeType?: string;
    /** Existing saved-document id — passed through to the save payload for update. */
    docId?: string;
    /** Server-assigned generation-lineage id — carried through so a new version
     *  saved from this drawer groups with its siblings (mirrors the Plate
     *  editor's persist path in ReportEditorRoute). */
    genId?: string;
    /** Save context. When absent (e.g. the read-only DataRoom viewer) the Save
     *  action is hidden and the drawer is view + download only. */
    assesseeOrgId?: string;
    investorOrgId?: string;
    createdBy?: string;
    documentType?: string;
    name?: string;
    description?: string;
    /** Called with the saved document after a successful save. */
    onSaved?: (doc: any) => void;
    /** Called with the saved document after a name/description edit succeeds.
     *  Unlike `onSaved` this does not close the drawer — only the caller's own
     *  list (e.g. the Reports table) needs refreshing; the drawer's header
     *  already reflects the new name. */
    onRenamed?: (doc: any) => void;
    /** Snapshot this sheet into Reports (Ask AICA / Data Room view contexts).
     *  When provided, this becomes the PRIMARY header action instead of Save —
     *  a view context never has both (you don't add a report to reports). */
    onAddToReports?: () => void;
    /** Loading state for the Add to Reports action, controlled by the caller. */
    addingToReports?: boolean;
    /** Briefly true right after a successful add — swaps the button to a
     *  checkmark + "Added" state, controlled by the caller. */
    addedToReports?: boolean;
}

// Fortune-Sheet's default toolbar with the one-click currency button removed.
// Currency formatting is still reachable via the "format" dropdown; we drop the
// quick button per product (no multi-currency need). currency="₹" makes the
// Format dropdown default to the Indian Rupee instead of the library default (¥).
const TOOLBAR_ITEMS = [
    'undo',
    'redo',
    'format-painter',
    'clear-format',
    '|',
    'percentage-format',
    'number-decrease',
    'number-increase',
    'format',
    '|',
    'font',
    '|',
    'font-size',
    '|',
    'bold',
    'italic',
    'strike-through',
    'underline',
    '|',
    'font-color',
    'background',
    'border',
    'merge-cell',
    '|',
    'horizontal-align',
    'vertical-align',
    'text-wrap',
    'text-rotation',
    '|',
    'freeze',
    'conditionFormat',
    'filter',
    'link',
    'image',
    'comment',
    'quick-formula',
    'dataVerification',
    'splitColumn',
    'locationCondition',
    'screenshot',
    'search',
];

/**
 * Editable spreadsheet drawer. Accepts either in-memory FortuneSheet[] (from
 * import or compose) or a presigned URL to fetch. Renders a Fortune-Sheet
 * grid (formulas + formula bar built in) inside the same BottomDrawer the
 * PlateEditor uses. Header actions: one "Download ▾" dropdown (.xlsx/.csv),
 * plus a primary action — "Add to Reports" when `onAddToReports` is passed
 * (view contexts), else "Save" when `canSave` (Reports edit/import).
 */
const SheetEditorDrawer: React.FC<SheetEditorDrawerProps> = ({
    open,
    onClose,
    fileName,
    sheets: inMemorySheets,
    fileUrl,
    mimeType,
    docId,
    genId,
    assesseeOrgId,
    investorOrgId,
    createdBy,
    documentType,
    name,
    description,
    onSaved,
    onRenamed,
    onAddToReports,
    addingToReports,
    addedToReports,
}) => {
    const {
        sheets,
        loading,
        error,
        saving,
        workbookRef,
        handleDownloadXlsx,
        handleDownloadCsv,
        handleSave,
        currentName,
        currentDescription,
        handleRename,
    } = useSheetEditorDrawerLogic({
        open,
        fileName,
        sheets: inMemorySheets,
        fileUrl,
        mimeType,
        docId,
        genId,
        assesseeOrgId,
        investorOrgId,
        createdBy,
        documentType,
        name,
        description,
        onSaved,
        onRenamed,
    });

    // Save is only available with a persistence context (Reports). The read-only
    // DataRoom viewer passes no assesseeOrgId → view + download only. The header's
    // "Edit name & description" affordance shares this same gate.
    const canSave = Boolean(assesseeOrgId);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const { fitColumnsToolbarItem } = useColumnAutofit({ workbookRef, wrapperRef, enabled: !loading && !error && !!sheets?.length });

    const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
    const downloadMenuRef = useRef<HTMLDivElement>(null);
    useOutsideClick(downloadMenuRef, () => setDownloadMenuOpen(false));

    const [renameOpen, setRenameOpen] = useState(false);

    const headerRight =
        !loading && !error ? (
            <div className={styles.actions}>
                <div className={styles.downloadGroup} ref={downloadMenuRef}>
                    <button
                        type="button"
                        className={styles.downloadBtn}
                        onClick={() => setDownloadMenuOpen((o) => !o)}
                        aria-haspopup="true"
                        aria-expanded={downloadMenuOpen}
                    >
                        Download <span className={styles.downloadCaret}>▾</span>
                    </button>
                    {downloadMenuOpen && (
                        <div className={styles.downloadPopover} role="menu" aria-label="Download options">
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.downloadPopoverItem}
                                onClick={() => {
                                    setDownloadMenuOpen(false);
                                    handleDownloadXlsx();
                                }}
                            >
                                Download as .xlsx
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className={styles.downloadPopoverItem}
                                onClick={() => {
                                    setDownloadMenuOpen(false);
                                    handleDownloadCsv();
                                }}
                            >
                                Download as .csv
                            </button>
                        </div>
                    )}
                </div>
                {onAddToReports ? (
                    <Button
                        type="button"
                        text={addedToReports ? <WhiteCheckIcon /> : 'Add to Workspace'}
                        variant="purple"
                        className={styles.headerBtn}
                        onClick={addedToReports ? () => {} : onAddToReports}
                        disabled={addingToReports}
                        loading={!addedToReports && addingToReports}
                    />
                ) : (
                    canSave && (
                        <Button
                            type="button"
                            text="Save and Close"
                            variant="purple"
                            className={styles.headerBtn}
                            onClick={async () => {
                                // Close only on a confirmed save — a failed save keeps the
                                // drawer (and the user's unsaved edits) open.
                                if (await handleSave()) onClose();
                            }}
                            disabled={saving}
                            loading={saving}
                        />
                    )
                )}
            </div>
        ) : null;

    // Use fileUrl as the workbook key on the fetch path; fall back to fileName
    // so in-memory sheets get a stable key that doesn't remount on every render.
    const workbookKey = fileUrl ?? fileName;

    // Report save-context (Reports table open/import) gets an editable title —
    // same "name + purple Edit pill" markup PlateDocumentDrawer uses for Reports.
    // Other contexts (Data Room, Ask AICA) keep the plain file name, view-only.
    const displayName = currentName || fileName;
    const drawerTitle = canSave ? (
        <div className={styles.headerLeft}>
            <div className={styles.titleBlock}>
                <span className={styles.titleText} title={displayName}>
                    {displayName}
                </span>
                <div
                    className={styles.editPill}
                    onClick={() => setRenameOpen(true)}
                    role="button"
                    tabIndex={0}
                    aria-label="Edit name and description"
                    title="Edit"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setRenameOpen(true);
                        }
                    }}
                >
                    <img src={editIconPurple} alt="" aria-hidden className={styles.editPillIcon} />
                    <span>Edit</span>
                </div>
            </div>
        </div>
    ) : (
        fileName
    );

    return (
        <>
            <BottomDrawer
                open={open}
                onClose={onClose}
                title={drawerTitle}
                headerRight={headerRight}
                className={styles.drawer}
                bodyClassName={styles.body}
                height="100vh"
            >
                {loading && (
                    <div className={styles.statusBox}>
                        <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                    </div>
                )}
                {error && (
                    <div className={styles.statusBox}>
                        <p>Unable to open this file for editing.</p>
                    </div>
                )}
                {!loading && !error && sheets && (
                    <div className={styles.workbookWrapper} ref={wrapperRef}>
                        <Workbook
                            key={workbookKey}
                            ref={workbookRef}
                            data={sheets}
                            toolbarItems={TOOLBAR_ITEMS}
                            customToolbarItems={[fitColumnsToolbarItem]}
                            lang="en"
                            currency="₹"
                            showToolbar
                            showFormulaBar
                            showSheetTabs
                            allowEdit
                            defaultRowHeight={MIN_ROW_HEIGHT}
                        />
                    </div>
                )}
            </BottomDrawer>
            {canSave && (
                <RenameReportDialog
                    open={renameOpen}
                    name={displayName}
                    description={currentDescription ?? ''}
                    onClose={() => setRenameOpen(false)}
                    onSave={(newName, newDescription) => {
                        setRenameOpen(false);
                        handleRename(newName, newDescription);
                    }}
                />
            )}
        </>
    );
};

export default SheetEditorDrawer;
